import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect} from 'react';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {UnsavedAutolinkerModel} from '../../../models/api/autolinkerModel';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {
  SettingsApplicationComponentsEnum,
  SettingsAutolinkersUrl,
  SettingsCustomFieldsPagesEnum,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
} from '../../../models/url/settingsUrlModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {useSettingsCustomFields} from '../../customFields/useCustomFields';
import {useFetchAutolinkers} from '../../fetchers/autolinkers';
import {AutolinkerMutationsEnum} from '../../fetchers/autolinkersMutations';
import {useSearchQuery} from '../hooks/useSearchQuery';
import {AutolinkerEditorSkeleton} from './autolinkerEditorSkeleton';
import {AutolinkerEditorWrapper} from './autolinkerEditorWrapper';
import {AutolinkersList} from './autolinkersList';
import {useAutolinkersInfoBox} from './settingsAutolinkersInfoBox';

/*
 * Props.
 */

interface SettingsAutolinkersProps {
  panelId: SettingsPanelId;
  url: SettingsAutolinkersUrl;
  application: LocalApplicationModel;
  setPageTitle: (title: React.ReactNode) => void;
  setPageActions: (title: React.ReactNode) => void;
}

/*
 * Component.
 */

export const SettingsAutolinkers: FC<SettingsAutolinkersProps> = (props) => {
  const {panelId, url, application, setPageTitle, setPageActions} = props;
  const {resourcePage, applicationId, autolinkerId} = url;

  const hasLocalApplication = Boolean(application);

  const {searchQuery, resetSearchQuery, onSearchQueryChange} = useSearchQuery(url);

  const company = useAppSelector(getCompany);

  const {
    items: autolinkers,
    loadMore,
    moreStatus,
    hasError,
    mutate,
  } = useFetchAutolinkers(application.id, searchQuery);

  const autolinker = autolinkerId ? autolinkers.find(({id}) => id === autolinkerId) : undefined;

  const customFields = useSettingsCustomFields();
  const {infoBox, infoBoxIcon} = useAutolinkersInfoBox();

  const {navigateToLink} = useLinkNavigation();
  const navigateTo = useCallback(
    (page: SettingsResourcePagesEnum, id?: number) =>
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.AUTOLINKERS,
          resourcePage: determineNewResourcePage(hasLocalApplication, page, id),
          applicationId,
          autolinkerId: id,
          searchQuery,
        }),
      ),
    [hasLocalApplication, applicationId, navigateToLink, searchQuery],
  );

  useEffect(() => {
    setPageTitle(renderPageTitle(url, application));
    setPageActions(
      url.resourcePage === SettingsResourcePagesEnum.OVERVIEW && !url.autolinkerId ? infoBoxIcon : null,
    );
  }, [application, infoBoxIcon, setPageActions, setPageTitle, url]);

  if (resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    if (autolinkerId) {
      if (customFields.status !== AsyncStatusesEnum.LOADED) {
        return <AutolinkerEditorSkeleton autolinker={autolinker} />;
      }
      return (
        <AutolinkerEditorWrapper
          panelId={panelId}
          autolinker={autolinker}
          // TODO: Migrate custom fields type to use CustomFieldResourceTypesEnum
          customFields={{
            [SettingsCustomFieldsPagesEnum.ACCOUNTS]: customFields.account,
            [SettingsCustomFieldsPagesEnum.CARDS]: customFields.card,
            [SettingsCustomFieldsPagesEnum.TEAMMATES]: customFields.teammate,
            [SettingsCustomFieldsPagesEnum.INBOXES]: customFields.inbox,
          }}
          isReadOnly
          navigateTo={navigateTo}
        />
      );
    }
    return (
      <AutolinkersList
        autolinkers={autolinkers}
        onLoadMore={loadMore}
        moreStatus={moreStatus}
        hasError={hasError}
        hasLocalApplication={hasLocalApplication}
        onDeleteAutolinkers={(autolinkerIds) =>
          mutate({
            type: AutolinkerMutationsEnum.DELETE,
            payload: {
              company,
              autolinkerIds,
            },
          })
        }
        infoBox={infoBox}
        searchQuery={searchQuery}
        resetSearchQuery={resetSearchQuery}
        onSearchQueryChange={onSearchQueryChange}
        navigateTo={navigateTo}
      />
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.NEW || resourcePage === SettingsResourcePagesEnum.EDIT) {
    if (customFields.status !== AsyncStatusesEnum.LOADED) {
      return <AutolinkerEditorSkeleton autolinker={autolinker} />;
    }

    const onSaveAutolinker = (unsavedAutolinker: UnsavedAutolinkerModel) => {
      if (!application) {
        return undefined;
      }

      if (autolinker) {
        return mutate({
          type: AutolinkerMutationsEnum.EDIT,
          payload: {
            id: autolinker.id,
            url: autolinker.url,
            autolinker: unsavedAutolinker,
          },
        });
      }

      return mutate({
        type: AutolinkerMutationsEnum.CREATE,
        payload: {
          application,
          autolinker: unsavedAutolinker,
        },
      });
    };

    return (
      <AutolinkerEditorWrapper
        panelId={panelId}
        autolinker={autolinker}
        // TODO: Migrate custom fields type to use CustomFieldResourceTypesEnum
        customFields={{
          [SettingsCustomFieldsPagesEnum.ACCOUNTS]: customFields.account,
          [SettingsCustomFieldsPagesEnum.CARDS]: customFields.card,
          [SettingsCustomFieldsPagesEnum.TEAMMATES]: customFields.teammate,
          [SettingsCustomFieldsPagesEnum.INBOXES]: customFields.inbox,
        }}
        onSaveAutolinker={onSaveAutolinker}
        navigateTo={navigateTo}
      />
    );
  }

  return null;
};

/*
 * Helpers.
 */

function renderPageTitle(url: SettingsAutolinkersUrl, application: LocalApplicationModel) {
  if (url.resourcePage === SettingsResourcePagesEnum.NEW) {
    return <Trans id="autolinkers-new-title">Create autolinker</Trans>;
  }
  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return <Trans id="autolinkers-edit-title">Edit autolinker</Trans>;
  }
  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW && url.autolinkerId) {
    return <Trans id="applications-details-overview-title">Autolinker Details</Trans>;
  }
  return application.name;
}

const determineNewResourcePage = (
  hasLocalApplication: boolean,
  page: SettingsResourcePagesEnum,
  id?: number,
) => {
  if (page === SettingsResourcePagesEnum.NEW) {
    return SettingsResourcePagesEnum.NEW;
  }

  if (hasLocalApplication && id) {
    return SettingsResourcePagesEnum.EDIT;
  }

  return SettingsResourcePagesEnum.OVERVIEW;
};
